import { getWindowObject } from '@eventbrite/feature-detection';
import type { EventDescriptionProps } from '../components/EventDetails/EventDescription';
import { FaqItem } from '../components/EventDetails/Widgets/Faq';
import type {
    EventListingsResponse,
    RefundPolicyProps,
    ServerEvent,
} from '../types';

export const getFaqsData = (
    eventDescription?: EventDescriptionProps,
): FaqItem[] => {
    const faqWidget = eventDescription?.structuredContent?.widgets.find(
        (widget) => widget.type === 'faqs',
    )?.data || { faqs: [] };
    return 'faqs' in faqWidget ? faqWidget?.faqs : [];
};

export const getRefundPolicy = (refund?: RefundPolicyProps) => {
    const { refundPolicyCode, refundPolicyIsNoRefunds } = refund || {};
    if (refundPolicyIsNoRefunds) return 'notAccepted';
    const acceptedPolicies = ['flexible', 'moderate', 'strict', 'custom'];
    if (refundPolicyCode && acceptedPolicies.includes(refundPolicyCode)) {
        return 'accepted';
    }
    return 'notDefined';
};

export const getParkingInfo = (
    highlights: EventListingsResponse['highlights'],
) => {
    if (highlights.freeParking) {
        return 'free';
    } else if (highlights.paidParking) {
        return 'paid';
    } else if (highlights.notAvailableParking) {
        return 'not_available';
    }
    return null;
};

export const checkExpressCheckoutCompatibility = (
    event: ServerEvent,
    isPaid: boolean,
): boolean => {
    const IOSDevice = checkIOsUserAgent();
    const applePayEnabled = checkApplePayEnabled();
    const eventEligible = checkExpressCheckoutEligibility(event, isPaid);
    return IOSDevice && applePayEnabled && eventEligible;
};

const checkIOsUserAgent = () => {
    const userAgent = getWindowObject('navigator')?.userAgent;
    return /iPad|iPhone/.test(userAgent ?? '');
};

const checkApplePayEnabled = () => {
    const applePaySession = getWindowObject('ApplePaySession');
    return applePaySession?.canMakePayments() || false;
};

const checkExpressCheckoutEligibility = (
    event: ServerEvent,
    isPaid: boolean,
): boolean => {
    const { salesStatus, compactCheckoutDisqualifications, ticketsInfo } =
        event;

    return (
        isPaid &&
        salesStatus.sales_status === 'on_sale' &&
        ticketsInfo.totalTicketsCount === 1 &&
        !compactCheckoutDisqualifications.has_discounts &&
        !compactCheckoutDisqualifications.reserved_seating &&
        !compactCheckoutDisqualifications.has_donations &&
        !compactCheckoutDisqualifications.has_addons
    );
};
